import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import client from "./client";
import type {
  Device, DeviceApplication, DeviceBrowserExtension, DeviceProgram,
  DeviceScreenlockPolicy, MacDeviceDrive, PaginatedResponse,
  UserDevicePolicy, WindowsDeviceDrive, WindowsSecurityCenter, WindowsSecurityProduct,
} from "./types";

export function useTenantDevices(tenantId: string, params?: Record<string, string>) {
  return useQuery<PaginatedResponse<Device>>({
    queryKey: ["devices", tenantId, params],
    queryFn: async () => {
      const { data } = await client.get(`/tenants/${tenantId}/devices/`, { params });
      return data;
    },
    enabled: !!tenantId,
  });
}

export function useDeviceDetail(tenantId: string, deviceId: string) {
  return useQuery<Device>({
    queryKey: ["devices", tenantId, deviceId],
    queryFn: async () => {
      const { data } = await client.get(`/tenants/${tenantId}/devices/${deviceId}/`);
      return data;
    },
    enabled: !!tenantId && !!deviceId,
  });
}

export function useCreateDevice(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<Device>) => {
      const { data } = await client.post(`/tenants/${tenantId}/devices/`, payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["devices", tenantId] }),
  });
}

export function useUpdateDevice(tenantId: string, deviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<Device>) => {
      const { data } = await client.patch(`/tenants/${tenantId}/devices/${deviceId}/`, payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["devices", tenantId] }),
  });
}

export function useDeleteDevice(tenantId: string, deviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await client.delete(`/tenants/${tenantId}/devices/${deviceId}/`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["devices", tenantId] }),
  });
}

export function useCorrelateDevice(tenantId: string, deviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (sourceId: string) => {
      const { data } = await client.post(
        `/tenants/${tenantId}/devices/${deviceId}/correlate/`,
        { source_id: sourceId }
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["devices", tenantId] }),
  });
}

export function useUnlinkSource(tenantId: string, deviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (sourceId: string) => {
      await client.post(`/tenants/${tenantId}/devices/${deviceId}/unlink-source/`, {
        source_id: sourceId,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["devices", tenantId] }),
  });
}

export function useAssignUserToDevice(tenantId: string, deviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { user_id: string; is_primary?: boolean }) => {
      const { data } = await client.post(
        `/tenants/${tenantId}/devices/${deviceId}/assign-user/`,
        payload
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["devices", tenantId, deviceId] }),
  });
}

export function useUnassignUserFromDevice(tenantId: string, deviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      await client.post(`/tenants/${tenantId}/devices/${deviceId}/unassign-user/`, {
        user_id: userId,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["devices", tenantId, deviceId] }),
  });
}

export function useSetPrimaryUser(tenantId: string, deviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const { data } = await client.post(
        `/tenants/${tenantId}/devices/${deviceId}/set-primary-user/`,
        { user_id: userId }
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["devices", tenantId, deviceId] }),
  });
}

// ---------------------------------------------------------------------------
// Sub-resource CRUD hooks
// ---------------------------------------------------------------------------

function useSubResourceMutation<TPayload>(
  tenantId: string, deviceId: string, resource: string,
  method: "post" | "patch" | "delete",
) {
  const qc = useQueryClient();
  const base = `/tenants/${tenantId}/devices/${deviceId}/${resource}`;
  return useMutation({
    mutationFn: async (args: { id?: string; payload?: TPayload }) => {
      if (method === "delete") {
        await client.delete(`${base}/${args.id}/`);
        return;
      }
      if (method === "patch") {
        const { data } = await client.patch(`${base}/${args.id}/`, args.payload);
        return data;
      }
      const { data } = await client.post(`${base}/`, args.payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["devices", tenantId, deviceId] }),
  });
}

// Applications
export function useCreateApplication(tenantId: string, deviceId: string) {
  return useSubResourceMutation<Partial<DeviceApplication>>(tenantId, deviceId, "applications", "post");
}
export function useUpdateApplication(tenantId: string, deviceId: string) {
  return useSubResourceMutation<Partial<DeviceApplication>>(tenantId, deviceId, "applications", "patch");
}
export function useDeleteApplication(tenantId: string, deviceId: string) {
  return useSubResourceMutation(tenantId, deviceId, "applications", "delete");
}

// Mac Drives
export function useCreateMacDrive(tenantId: string, deviceId: string) {
  return useSubResourceMutation<Partial<MacDeviceDrive>>(tenantId, deviceId, "mac-drives", "post");
}
export function useUpdateMacDrive(tenantId: string, deviceId: string) {
  return useSubResourceMutation<Partial<MacDeviceDrive>>(tenantId, deviceId, "mac-drives", "patch");
}
export function useDeleteMacDrive(tenantId: string, deviceId: string) {
  return useSubResourceMutation(tenantId, deviceId, "mac-drives", "delete");
}

// Browser Extensions
export function useCreateBrowserExtension(tenantId: string, deviceId: string) {
  return useSubResourceMutation<Partial<DeviceBrowserExtension>>(tenantId, deviceId, "browser-extensions", "post");
}
export function useUpdateBrowserExtension(tenantId: string, deviceId: string) {
  return useSubResourceMutation<Partial<DeviceBrowserExtension>>(tenantId, deviceId, "browser-extensions", "patch");
}
export function useDeleteBrowserExtension(tenantId: string, deviceId: string) {
  return useSubResourceMutation(tenantId, deviceId, "browser-extensions", "delete");
}

// Screenlock Policies
export function useCreateScreenlockPolicy(tenantId: string, deviceId: string) {
  return useSubResourceMutation<Partial<DeviceScreenlockPolicy>>(tenantId, deviceId, "screenlock-policies", "post");
}
export function useUpdateScreenlockPolicy(tenantId: string, deviceId: string) {
  return useSubResourceMutation<Partial<DeviceScreenlockPolicy>>(tenantId, deviceId, "screenlock-policies", "patch");
}
export function useDeleteScreenlockPolicy(tenantId: string, deviceId: string) {
  return useSubResourceMutation(tenantId, deviceId, "screenlock-policies", "delete");
}

// User Device Policies
export function useCreateUserPolicy(tenantId: string, deviceId: string) {
  return useSubResourceMutation<Partial<UserDevicePolicy> & { assignment_id?: string; settings_requires_password?: boolean; settings_screen_sleep_timeout_ms?: number }>(tenantId, deviceId, "user-policies", "post");
}
export function useUpdateUserPolicy(tenantId: string, deviceId: string) {
  return useSubResourceMutation<Partial<UserDevicePolicy> & { settings_requires_password?: boolean; settings_screen_sleep_timeout_ms?: number }>(tenantId, deviceId, "user-policies", "patch");
}
export function useDeleteUserPolicy(tenantId: string, deviceId: string) {
  return useSubResourceMutation(tenantId, deviceId, "user-policies", "delete");
}

// Programs (Windows)
export function useCreateProgram(tenantId: string, deviceId: string) {
  return useSubResourceMutation<Partial<DeviceProgram>>(tenantId, deviceId, "programs", "post");
}
export function useUpdateProgram(tenantId: string, deviceId: string) {
  return useSubResourceMutation<Partial<DeviceProgram>>(tenantId, deviceId, "programs", "patch");
}
export function useDeleteProgram(tenantId: string, deviceId: string) {
  return useSubResourceMutation(tenantId, deviceId, "programs", "delete");
}

// Windows Drives
export function useCreateWindowsDrive(tenantId: string, deviceId: string) {
  return useSubResourceMutation<Partial<WindowsDeviceDrive>>(tenantId, deviceId, "windows-drives", "post");
}
export function useUpdateWindowsDrive(tenantId: string, deviceId: string) {
  return useSubResourceMutation<Partial<WindowsDeviceDrive>>(tenantId, deviceId, "windows-drives", "patch");
}
export function useDeleteWindowsDrive(tenantId: string, deviceId: string) {
  return useSubResourceMutation(tenantId, deviceId, "windows-drives", "delete");
}

// Security Products (Windows)
export function useCreateSecurityProduct(tenantId: string, deviceId: string) {
  return useSubResourceMutation<Partial<WindowsSecurityProduct>>(tenantId, deviceId, "security-products", "post");
}
export function useUpdateSecurityProduct(tenantId: string, deviceId: string) {
  return useSubResourceMutation<Partial<WindowsSecurityProduct>>(tenantId, deviceId, "security-products", "patch");
}
export function useDeleteSecurityProduct(tenantId: string, deviceId: string) {
  return useSubResourceMutation(tenantId, deviceId, "security-products", "delete");
}

// Windows Security Center (singleton)
export function useCreateSecurityCenter(tenantId: string, deviceId: string) {
  return useSubResourceMutation<Partial<WindowsSecurityCenter>>(tenantId, deviceId, "security-center", "post");
}
export function useUpdateSecurityCenter(tenantId: string, deviceId: string) {
  return useSubResourceMutation<Partial<WindowsSecurityCenter>>(tenantId, deviceId, "security-center", "patch");
}
export function useDeleteSecurityCenter(tenantId: string, deviceId: string) {
  return useSubResourceMutation(tenantId, deviceId, "security-center", "delete");
}

